/* Statuses of assets.*/
export interface AssetUploadStatuses {
  /* Statuses of files. */
  files: FileUploadStatus[]

  /* Statuses of file sequences. */
  fileSequences: FileSequenceUploadStatus[];

  /* Statuses of directories. */
  directories: DirectoryUploadStatus[]

  /* Messages to report. Typically warning or errors related to processing files. */
  messages: string[]
};

/* Status of a directory asset. */
export interface DirectoryUploadStatus {
  /* Directory in which the asset files are found. */
  directory: string,

  /* Is the directory ready to be used. */
  ready: boolean

  /* Files within the directory. */
  files?: string[]
};

/* Status of a file sequence. */
export interface FileSequenceUploadStatus {
  /* Pattern that files within the sequence are expected to follow.*/
  filePathPattern: string,

  /* Index of the required file within the sequence. */
  requiredIndex: string,

  /* Is the sequence ready for use. */
  ready: boolean,

  /* Files within the sequence that have been uploaded. */
  files: string[]
}

/* Status of a file asset.*/
export interface FileUploadStatus {
  /* Expected path of the asset.*/
  path: string,

  /*Is the asset ready to be used.*/
  ready: boolean
};

/** Template which can be selected for work orders. */
export interface Template {
  /** ID of the template. */
  templateID?: number;

  /** Name of the template. */
  name: string;

  /** Is the template flagged as deleted. */
  deleted: boolean;

  /** Date and time that template was created. */
  creationTime?: Date;

  /** Current template status. */
  status?: TemplateStatus;

  /** Cavalry scene file data. */
  cavalryProject: any;

  /** File assets */
  files?: string[];

  /* File sequence assets. */
  fileSequences?: TemplateFileSequence[];

  /** CSV parameters. */
  csvParameters?: TemplateCsvParameter[];

  /** Smart folder parameters. */
  smartFolderParameters?: TemplateSmartFolderParameter[];
};

/** CSV parameter required by a template. */
export interface TemplateCsvParameter {
  /** Id of the CSV parameter. */
  templateCsvParameterId: number;

  /** Name given to the parameter. */
  name: string;

  /** Index of the column to which this parameter relates. */
  columnIndex: number;

  /** Path which should contain the CSV file with the parameter. */
  assetPath: string;

  /** Node ID of the parameter. */
  assetNodeID: string;

  /** Smart folders which reference this CSV parameter. */
  smartFolders: TemplateCsvParameterSmartFolder[];
};

/** Smart folder for a CSV parameter required by a template. */
export interface TemplateCsvParameterSmartFolder {
  /** Path which should contain smart folder items. */
  assetPath: string;

  /** Node ID of the parameter. */
  assetNodeID: string;
};

/* File sequence associated with a template. */
export interface TemplateFileSequence {
  /* Pattern that the file names in the sequence should follow. */
  assetPathPattern: string,

  /* Index required by the sequence. */
  requiredIndex: string
}

/** CSV parameter required by a template. */
export interface TemplateSmartFolderParameter {
  /** Path which should contain smart folder items. */
  assetPath: string;

  /** Node ID of the parameter. */
  assetNodeID: string;
};

/* Enumeration of supported template status types. */
export enum TemplateStatusType {
  PendingAssets = "PendingAssets",
  Ready = "Ready"
};

/* Enumeration of fields that template search results can be sorted by. */
export enum TemplateSortField {
  CreationTime = "CreationTime",
  Name = "Name",
  LastStatusTime = "LastStatusTime",
  LastStatus = "LastStatus"
};

/** Status applied to a template. */
export interface TemplateStatus {
  /** Type of the status. */
  templateStatusType: TemplateStatusType;

  /** Time the status was applied. */
  statusTime: Date;
};

/** A user. */
export interface User {
  /** ID of the user. */
  userID?: number;

  /** Email address of the user. */
  emailAddress?: string;

  /** Date and time that the user was created. */
  creationTime?: Date;
  /** Last date and time that the user logged in. */

  lastPasswordSetTime?: Date;

  /** Last date and time that the user logged in. */
  lastLoginTime?: Date;
}

/** A work order. */
export interface WorkOrder {
  /** ID of the work order. */
  workOrderID?: number;

  /** ID of the template used. */
  templateID?: number;

  /** Template used. */
  template?: Template;

  /** Name of the work order. */
  name: string;

  /** Number of renders expected. */
  renderCount: number;

  /** Current status of the work order. */
  status?: WorkOrderStatus;

  /** Date and time at which the order was created. */
  creationTime?: Date;
}

/** A result file produced by a work order. */
export interface WorkOrderResult {
  /** Name of the result file. */
  name: string;

  /** Size, in bytes, of the result file. */
  size: number;
}

/* Enumeration of supported work order status types. */
export enum WorkOrderStatusType {
  Archived = "Archived",
  Failed = "Failed",
  Done = "Done",
  PendingParameters = "PendingParameters",
  PendingDynamicAssets = "PendingDynamicAssets",
  QueuedForProcessing = "QueuedForProcessing",
  QueuedForRendering = "QueuedForRendering",
  Rendering = "Rendering",
  PreparingResults = "PreparingResults"
};

/* Enumeration of fields that work order search results can be sorted by. */
export enum WorkOrderSortField {
  CreationTime = "CreationTime",
  Name = "Name",
  LastStatusTime = "LastStatusTime",
  LastStatus = "LastStatus"
};

/** Status applied to a work order. */
export interface WorkOrderStatus {
  /** Type of the status. */
  workOrderStatusType: WorkOrderStatusType;

  /** Time the status was applied. */
  statusTime: Date;
};